import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Query } from "../generated/nest-graphql";
import { GET_MY_PART_ORDER_JOBS } from "../graphql/queries/getMyPartOrderJobs";
import { PartOrderJobOption } from "../components/Parts/AddPartOrderDialog";

// The schedule, not part orders, is the source of "my jobs today" for the picker.
export const usePartOrderJobOptions = ({
  startRange,
  endRange,
  skip,
}: {
  startRange: Date;
  endRange: Date;
  skip: boolean;
}) => {
  const { data, refetch } = useQuery<Query>(GET_MY_PART_ORDER_JOBS, {
    variables: { startRange, endRange },
    // A job withdrawn since the last visit must leave the picker without a reload.
    fetchPolicy: "cache-and-network",
    skip,
  });

  const jobOptions: PartOrderJobOption[] = useMemo(() => {
    const seen = new Map<string, PartOrderJobOption>();
    for (const appointment of data?.getMyAppointments ?? []) {
      const job = appointment?.job;
      // A withdrawn job keeps its appointment on the day, but nothing should be ordered for it.
      if (!job?.id || job.isWithdrawn || seen.has(job.id)) continue;
      const vehicle = job.vehicleInfo;
      const descriptor = vehicle?.year ? `${vehicle.year} ${vehicle.make ?? ""} ${vehicle.model ?? ""}`.trim() : "";
      seen.set(job.id, {
        jobId: job.id,
        jobNumber: job.jobNumber,
        contactId: job.contact?.id,
        label: `#${job.jobNumber ?? "—"}${descriptor ? ` · ${descriptor}` : ""}`,
      });
    }

    return Array.from(seen.values());
  }, [data]);

  return { jobOptions, refetch };
};
